// src/components/RhinoVOIPContainer.jsx
import React, { useState, useEffect } from 'react';
import RhinoVOIPDialPad from './RhinoVOIPDialPad';
import RhinoVOIPRecentCalls from './RhinoVOIPRecentCalls';
import RhinoVOIPVoicemails from './RhinoVOIPVoicemails';
import useVoipBadgeCounts from '../hooks/useVoipBadgeCounts';
import useLastRecentsViewedAt from '../hooks/useLastRecentsViewedAt';

import '../assets/styles/rhinovoip-container.less';
import voicemailIcon from '../assets/images/voip/tab-voicemail.svg';
import recentsIcon from '../assets/images/voip/tab-recents.svg';
import dialpadIcon from '../assets/images/voip/tab-dialpad.svg';

export default function RhinoVOIPContainer() {
  const [activeTab, setActiveTab] = useState('dialpad');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [autoPlayVoicemailId, setAutoPlayVoicemailId] = useState(null);

  const { newMissedCount, unreadVmCount } = useVoipBadgeCounts();
  const { markViewedNow } = useLastRecentsViewedAt();

  // Reset the missed-call badge only when the member explicitly clicks the
  // Recents tab. The desktop auto-switch onto Recents on mount (see resize
  // handler below) bypasses this so the badge survives until the member
  // intentionally views the tab.
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === 'recents') markViewedNow();
  };

  const renderIconWithBadge = (icon, alt, count) => (
    <span className="voip-tab__icon-wrap">
      <img src={icon} alt={alt} className="tab-icon" />
      {count > 0 && <span className="voip-tab__badge">{count}</span>}
    </span>
  );

  const navigateToVoicemail = (voicemailMessageUuid) => {
    setAutoPlayVoicemailId(voicemailMessageUuid);
    setActiveTab('voicemail');
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile && activeTab === 'dialpad') {
        setActiveTab('recents'); // <-- fix
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab]);

  const renderContent = () => {
    switch (activeTab) {
      case 'recents':
        return <RhinoVOIPRecentCalls onNavigateToVoicemail={navigateToVoicemail} />;
      case 'voicemail':
        return <RhinoVOIPVoicemails autoPlayVoicemailId={autoPlayVoicemailId} onAutoPlayConsumed={() => setAutoPlayVoicemailId(null)} />;
      default:
        return <RhinoVOIPDialPad />;
    }
  };

  return (
    <div className={`voip-container ${isMobile ? 'mobile' : 'desktop'}`}>
      {isMobile ? (
        <>
          <div className="voip-content">{renderContent()}</div>
          <div className="voip-tabs bottom-tabs">
            <button type="button" className={activeTab === 'dialpad' ? 'active' : ''} onClick={() => setActiveTab('dialpad')}>
              <img src={dialpadIcon} alt="Dialpad" className="tab-icon" />
              <span>Dialpad</span>
            </button>
            <button type="button" className={activeTab === 'recents' ? 'active' : ''} onClick={() => handleTabClick('recents')}>
              {renderIconWithBadge(recentsIcon, 'Recents', newMissedCount)}
              <span>Recents</span>
            </button>
            <button type="button" className={activeTab === 'voicemail' ? 'active' : ''} onClick={() => setActiveTab('voicemail')}>
              {renderIconWithBadge(voicemailIcon, 'Voicemail', unreadVmCount)}
              <span>Voicemail</span>
            </button>
          </div>
        </>
      ) : (
        <div className="voip-layout">
          <div className="voip-left">
            <RhinoVOIPDialPad />
          </div>
          <div className="voip-right">
            <div className="voip-tabs top-tabs">
              <button type="button" className={activeTab === 'recents' ? 'active' : ''} onClick={() => handleTabClick('recents')}>
                {renderIconWithBadge(recentsIcon, 'Recents', newMissedCount)}
                <span>Recents</span>
              </button>
              <button type="button" className={activeTab === 'voicemail' ? 'active' : ''} onClick={() => setActiveTab('voicemail')}>
                {renderIconWithBadge(voicemailIcon, 'Voicemail', unreadVmCount)}
                <span>Voicemail</span>
              </button>
            </div>
            <div className="voip-panel-content">{renderContent()}</div>
          </div>
        </div>
      )}
    </div>
  );
}
